import { useEffect, useMemo, useState } from 'react';
import { useWorldStore } from '../state/worldStore';
import { interiorFloor } from '../state/interiorFloor';
import { seatsFor } from '../services/seats';
import { listWorldObjects } from '../services/worldObjects';

// useRoomSeats — the chairs in the room you are standing in, resolved once.
//
// This was written twice (the dock counted them, the 3D layer resolved them) and
// the two copies HAD to agree: the server hands out a seat INDEX, so if the
// panel's count and the drawn chairs come from lists in different orders, or of
// different lengths, somebody sits in the wrong place or vanishes. One hook, one
// list, one order.
//
// ── TWO SOURCES, BECAUSE ROOMS COME TWO WAYS ────────────────────────────────
// A BUILT room (the salon) carries seat affordances on its model. A SCANNED room
// (the Listening Room is a Marble splat) carries nothing readable at all, so its
// seats are placed by hand in ⚙ as `world_objects` rows. Placed wins — see
// services/seats.js.
//
// ── AND WHY IT WATCHES THE STORE ────────────────────────────────────────────
// The fetch answers once per room. Placing a seat in ⚙ while standing in that
// room therefore changed nothing until you walked out and back in — which is
// the exact order anybody sets a room up in, and it read as "the feature is
// missing" rather than "the count is stale". Saved rows land in the store, so
// the store is what re-triggers the resolve.
export default function useRoomSeats(interior) {
  const [seats, setSeats] = useState([]);
  const objects = useWorldStore((s) => s.worldObjects);

  // Seats for THIS room that exist on the server (a numeric id means saved —
  // an unsaved one would offer capacity nobody else's client can resolve).
  const saved = useMemo(() => objects.filter((o) => (
    o.zone_id === interior && o.kind === 'seat' && typeof o.object_id === 'number' && Array.isArray(o.pos)
  )), [objects, interior]);
  // A cheap signature so a nudge-and-save re-resolves but an unrelated store
  // write does not.
  // bodyHeight is in the signature for the same reason position and rotation
  // are: it changes what gets DRAWN in that chair, and leaving it out would mean
  // sizing a person in ⚙ did nothing until you walked out of the room and back
  // in — the exact staleness the note above exists to describe.
  const savedKey = saved
    .map((o) => `${o.object_id}:${o.slot}:${o.pos.join(',')}:${o.rotY}:${o.data?.bodyHeight ?? ''}`)
    .join('|');

  useEffect(() => {
    if (!interior) { setSeats([]); return undefined; }
    let dead = false;
    let tries = 0;
    let timer = null;
    // Whatever the store already holds is the freshest answer; the fetch covers
    // the ordinary case where no admin tool has ever been opened.
    let placed = saved;
    if (!placed.length) listWorldObjects(interior, 'seat').then((rows) => { if (!dead) placed = rows || []; });
    // Retried because the model streams in: on the frame you walk through the
    // door a built room is often still arriving, and resolving once would report
    // a salon with no seats and hide the feature exactly where it belongs.
    const scan = () => {
      if (dead) return;
      const found = seatsFor(interiorFloor.object, placed);
      if (found.length || ++tries > 12) {
        setSeats(found);
        if (!found.length) console.log(`[seats] none in "${interior}" — none on the model, none placed in ⚙`);
        return;
      }
      timer = setTimeout(scan, 400);
    };
    scan();
    return () => { dead = true; clearTimeout(timer); };
  }, [interior, savedKey]); // eslint-disable-line react-hooks/exhaustive-deps

  return seats;
}
